// Arena head movement (spec §2, backlog S-1, S-2): ONE function used by the server sim and by the
// client's own-head prediction (backlog C-2), so both integrate exactly the same way.
//
// Model (owner + territory.md §1): constant speed `speed` cells/s; the heading turns toward the
// target heading by the shorter way at most `turnRateRad · dt` per tick (an exact-opposite target
// turns toward +angle, deterministically); the head then moves `speed · dt` along the new heading;
// the wall removes the velocity component into the rim (the circle normal, or the square's
// axis), so the head slides and stops in a corner, never dies there. Frozen heads do not move.
//
// Pure and deterministic: no randomness, no clocks; float maths only in the order written here.
//
// Owner: lane S (built, with its unit tests S-1 a–c, S-2 in packages/server/test/arena), lane C
// calls it.

import { arenaCircle, type ArenaGridShape, SQUARE_HEAD_MARGIN } from './grid.js';
import { wrapAngle } from './heading.js';

const TAU = Math.PI * 2;

/** Mutable head kinematics (cells, radians). */
export interface ArenaHeadMotion {
  x: number;
  y: number;
  /** Current heading, radians in [0, 2π). */
  heading: number;
}

export interface ArenaMoveParams {
  /** Cells per second (WELCOME `speed`, config `arena.move.speed`). */
  readonly speed: number;
  /** Radians per second (from `arena.move.turnRateDeg`). */
  readonly turnRateRad: number;
  /** Seconds per tick (1 / tickRate). */
  readonly dt: number;
}

/**
 * The heading after one tick of turning from `heading` toward `target` (both radians) by at most
 * `maxStep` radians; wrapped into [0, 2π). The shorter way round; an exactly opposite target
 * turns toward +angle (deterministic, backlog S-1).
 */
export function turnToward(heading: number, target: number, maxStep: number): number {
  let d = (target - heading) % TAU; // (−2π, 2π)
  if (d < 0) d += TAU; // [0, 2π)
  if (d > Math.PI) d -= TAU; // (−π, π]: exactly π stays +π
  if (!(maxStep > 0)) return wrapAngle(heading);
  if (d <= maxStep && d >= -maxStep) return wrapAngle(target);
  return wrapAngle(heading + (d > 0 ? maxStep : -maxStep));
}

/**
 * Advances `head` by one tick toward `targetHeading` (radians) inside `grid` (wall slide). Returns
 * `head` for chaining. The caller rasterises the segment old → new (sim: supercover cell entries).
 *
 * Order: turn (turnToward, `turnRateRad · dt`), then move `speed · dt` along the new heading, then
 * the wall: a circle projects a position past `arenaCircle(grid).headR` back onto that circle
 * (the radial component is removed, the tangential part kept: a slide along the rim); a square
 * clamps each axis to `[SQUARE_HEAD_MARGIN, side − SQUARE_HEAD_MARGIN]` (a slide, a stop in a
 * corner). The heading itself is never changed by the wall.
 */
export function stepHead(
  head: ArenaHeadMotion,
  targetHeading: number,
  p: ArenaMoveParams,
  grid: ArenaGridShape,
): ArenaHeadMotion {
  const h = turnToward(head.heading, targetHeading, p.turnRateRad * p.dt);
  head.heading = h;
  const step = p.speed * p.dt;
  let x = head.x + Math.cos(h) * step;
  let y = head.y + Math.sin(h) * step;
  if (grid.shape === 'square') {
    const lo = SQUARE_HEAD_MARGIN;
    x = x < lo ? lo : x > grid.cols - lo ? grid.cols - lo : x;
    y = y < lo ? lo : y > grid.rows - lo ? grid.rows - lo : y;
  } else {
    const c = arenaCircle(grid);
    const ex = x - c.cx;
    const ey = y - c.cy;
    const d2 = ex * ex + ey * ey;
    if (d2 > c.headR * c.headR) {
      const k = c.headR / Math.sqrt(d2);
      x = c.cx + ex * k;
      y = c.cy + ey * k;
    }
  }
  head.x = x;
  head.y = y;
  return head;
}
